import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";

import noop from "lodash/noop";

import { BenchmarkGranularity } from "app/common/benchmark/constants";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { useSubscriptions } from "../../hooks/settings/useSubscriptions";

import type { SubscriptionProviderReturnType } from "./types";

const initialData: SubscriptionProviderReturnType = {
    onSubscribe: noop,
    onUnsubscribe: noop,
    onSwitch: noop,
    onSelect: noop,
    onSave: noop,
    isSwitchedAlert: false,
    isSwitchedNewsAlerts: false,
    isSwitchedDigest: false,
    digestGranularity: BenchmarkGranularity.WEEKLY,
    alertsGranularity: BenchmarkGranularity.WEEKLY,
    newsAlertsGranularity: GranularityNames.DAILY,
    isLoading: false,
    isSubscribing: false,
    isAlertsAvailable: false,
    isNewsAlertsAvailable: false,
    isChanged: false,
};

const SubscriptionContext = createContext(initialData);

type SubscriptionProviderSchema = {
    benchmarkId: string;
    activationId: string;
    isViewerMode: boolean;
};

export const SubscriptionProvider = ({
    children,
    isViewerMode,
    activationId,
    benchmarkId,
}: PropsWithChildren<SubscriptionProviderSchema>) => {
    const subscriptionsData = useSubscriptions({ isViewerMode, activationId, benchmarkId });

    return <SubscriptionContext.Provider value={subscriptionsData}>{children}</SubscriptionContext.Provider>;
};

export const useSubscriptionContext = () => {
    const context = useContext(SubscriptionContext);

    if (!context) {
        throw new Error("useSubscriptionContext must be used within a SubscriptionProvider");
    }

    return context;
};
